import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRealtimeStatus } from "@/contexts/RealtimeContext";
import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Indicador visual de status da conexão realtime
 * Mostra: Online (verde), Offline (vermelho), Reconectando (amarelo)
 * Usa contexto centralizado para evitar múltiplas subscriptions
 * OTIMIZAÇÃO: Tooltip mostra última sincronização
 */
export const RealtimeStatusIndicator = () => {
  const { isConnected, isOnline, reconnectAttempts, lastSync } = useRealtimeStatus();

  const lastSyncText = lastSync 
    ? `Última atualização: ${formatDistanceToNow(lastSync, { locale: ptBR, addSuffix: true })}`
    : 'Sincronizando...';

  // Offline no navegador
  if (!isOnline) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="destructive" className="flex items-center gap-1 cursor-default">
            <WifiOff className="h-3 w-3" />
            Offline
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">Sem conexão com a internet</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Reconectando
  if (!isConnected && reconnectAttempts > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="flex items-center gap-1 bg-yellow-500/10 text-yellow-700 border-yellow-500/20 cursor-default">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Reconectando...
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">Tentativa {reconnectAttempts} • {lastSyncText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Online
  if (isConnected) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="flex items-center gap-1 bg-green-500/10 text-green-700 border-green-500/20 cursor-default">
            <Wifi className="h-3 w-3" />
            Online
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">{lastSyncText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Conectando (estado inicial)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="secondary" className="flex items-center gap-1 cursor-default">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Conectando...
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">Estabelecendo conexão...</p>
      </TooltipContent>
    </Tooltip>
  );
};
